import * as React from "react";
import { useParams } from "react-router-dom";

import { PageRenderer } from "@/components/builder/runtime-renderer";
import { RuntimeDataProvider } from "@/components/builder/runtime-data-provider";
import { PagePermissionGuard } from "@/components/page-permission-guard";
import { Skeleton } from "@/components/ui/skeleton";
import { initAnalytics } from "@/lib/analytics";
import { apiClient } from "@/services/api";
import type { AppSchema, Page } from "@shared/types";

interface AppRunLoaderState {
  loading: boolean;
  error: string | null;
  schema: AppSchema | null;
}

function useAppSchema(appId: string | undefined): AppRunLoaderState {
  const [state, setState] = React.useState<AppRunLoaderState>({ loading: true, error: null, schema: null });

  React.useEffect(() => {
    if (!appId) {
      setState({ loading: false, error: "缺少应用 ID", schema: null });
      return;
    }

    let cancelled = false;
    setState({ loading: true, error: null, schema: null });

    apiClient
      .get(`/apps/${appId}`)
      .then((response) => {
        if (cancelled) return;
        const payload = response.data as { app_schema: AppSchema };
        setState({ loading: false, error: null, schema: payload.app_schema });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "加载应用失败";
        setState({ loading: false, error: message, schema: null });
      });

    return () => {
      cancelled = true;
    };
  }, [appId]);

  return state;
}

function findPage(schema: AppSchema, pageId?: string): Page | undefined {
  if (pageId) {
    return schema.pages.find((p) => p.id === pageId || p.route === `/${pageId}`);
  }
  return schema.pages[0];
}

export function AppRunPage() {
  const { appId, pageId } = useParams<{ appId: string; pageId?: string }>();
  const { loading, error, schema } = useAppSchema(appId);

  const page = schema ? findPage(schema, pageId) : undefined;
  const pageRoute = page?.route || page?.name;

  // 数据分析埋点：runtime-renderer 独立运行模式接入（PV 上报 + 停留时长结算）
  React.useEffect(() => {
    if (appId && pageRoute) {
      initAnalytics(appId, pageRoute);
    }
  }, [appId, pageRoute]);

  if (loading) {
    // 画布加载态：Skeleton 占位（标题栏 + 内容区）
    return (
      <div className="h-full space-y-3 p-6" data-testid="app-run-loading">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-2/3" />
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-destructive">
        <p>无法加载应用</p>
        <p className="text-xs text-muted-foreground">{error || "未知错误"}</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-destructive">
        页面不存在
      </div>
    );
  }

  return (
    <RuntimeDataProvider>
      <div className="h-full bg-background">
        <PagePermissionGuard allowedRoles={page.permissions}>
          <PageRenderer page={page} ctx={{ permissions: schema.permissions }} />
        </PagePermissionGuard>
      </div>
    </RuntimeDataProvider>
  );
}
